<template>
  <div v-click-outside="hideAutoComplete">
    <div class="basic-email-input-container">
      <Field v-slot="{ field }" ref="validation-provider-email" v-model="mutatedValue" :label="label" validateOnInput
        :name="name" :rules="`${validation}|email`" @input="onInput($event.target.value)">
        <div class="bordered-bottom">
          <label v-if="displayLabel">{{ label }} {{ validation.split('|').includes('required') ? '*' : '' }}</label>
          <input :ref="name" type="email" class="bordered-bottom" :autocomplete="autocompletePropsValue" v-bind="field"
            :placeholder="`${placeholder} ${!displayLabel && validation.split('|').includes('required') ? '*' : ''}`"
            :readonly="isReadOnly" @keyup="autocompleteOnKeyUp" @blur="checkEmailRecipient ? checkRecipient() : ''">
          <img v-if="![null, undefined, ''].includes(mutatedValue)" class="form-generator-clear-button"
            src="@/assets/icons/cancel.svg" alt="clear input" @click="clearInput">
        </div>
        <ErrorMessage class="error-msg" :name="name" />
        <span v-if="warning" class="warning-msg">{{ warning }}</span>
      </Field>
    </div>
    <div v-if="autocomplete_.list && autocomplete_.active" id="autocompleteList">
      <ul @blur="autocomplete_.active = false">
        <li v-for="item in autocomplete_.list" :key="item.id" @click="selectAutocomplete(item)">
          {{ item }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
/**
 * Props:
 * label: label displayed (string),
 * name: input name (string)
 * value: the value of the input that need to be displayed on load (string)
 */
import { autoCompleteService } from '@/services/autocomplete.service'
import vClickOutside from 'click-outside-vue3'
import { Field, ErrorMessage } from 'vee-validate'
import _ from 'lodash'
import { customerService } from '@/services/customer.service'
import { EventBus } from '@/event-bus'

export default {
  name: 'BasicEmailInput',
  components: { Field, ErrorMessage },
  expose: ['hideAutoComplete', 'clearInput'],
  directives: {
    clickOutside: vClickOutside.directive
  },
  props: {
    label: String,
    validation: [String, Boolean],
    placeholder: String,
    defaultValue: [String, Array],
    name: String,
    displayLabel: {
      type: Boolean,
      default: false
    },
    getChildByName: {
      type: Function,
      default: null
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: Object,
      default: function () {
        return {
          enable: false,
          type: null,
          field: null,
          min_length: 3,
          active: false,
          list: []
        }
      }
    }
  },
  emits: ['addData'],
  data() {
    return {
      mutatedValue: this.defaultValue,
      autocomplete_: {},
      valueHasBeenAutocompleted: false,
      checkEmailRecipient: this.$appConfigurationStore?.appConfiguration?.modules?.customer?.create.checkEmailRecipient,
      warning: null,
      autocompletePropsValue: 'off'
    }
  },
  created() {
    let chrome = /chrom(e|ium)/.test(navigator.userAgent.toLowerCase())
    if (chrome) this.autocompletePropsValue = 'NoAutocompletion'
  },
  mounted() {
    this.mutatedValue
      ? this.$emit('addData', { value: this.mutatedValue, name: this.name })
      : this.$emit('addData', { value: '', name: this.name })
    this.autocomplete_ = this.autocomplete
    //Disable autocomplete for datacapture
    this.autocomplete_.enable = false
  },
  methods: {
    onInput(value = null) {
      this.$emit('addData', { value, name: this.name })
      if (this.valueHasBeenAutocompleted) {
        this.valueHasBeenAutocompleted = false
      }
      this.warning = null
    },
    /* event for autocomplete field */
    autocompleteOnKeyUp: _.debounce(function () {
      /* if autocomplete enabled on field */
      if (this.autocomplete_.enable) {
        /* check if field length is ok for launch autocomplete */
        if (
          this.mutatedValue &&
          this.mutatedValue.length < Math.min(this.autocomplete.min_length, parseInt(this.mutatedMaxLength))
        ) {
          this.autocomplete_['list'] = []
          this.autocomplete_.active = false
          return
        }
        const name = this.getChildByName('lastname')
        const firstName = this.getChildByName('firstname')
        autoCompleteService
          .autoCompleteEmail(this.mutatedValue, name, firstName)
          .then(response => {
            if (response.length > 0) {
              if (this.autocomplete.field === this.name) {
                this.autocomplete_.active = true
                this.autocomplete_['list'] = response
              } else {
                this.autocomplete_.active = false
                this.autocomplete_['list'] = []
              }
            }
          })
          .catch(() => {
            this.autocomplete_['list'] = []
            this.autocomplete_.active = false
          })
      }
    }, 500),
    selectAutocomplete(item) {
      this.$refs[this.name].focus()
      this.mutatedValue = item
      this.valueHasBeenAutocompleted = true
      this.$emit('addData', { value: item, name: this.name })
      this.autocomplete_.active = false
    },
    hideAutoComplete() {
      this.autocomplete_.active = false
    },
    clearInput() {
      this.mutatedValue = ''
      this.warning = null
      this.onInput()
    },
    async checkRecipient() {
      this.warning = null
      if (this.mutatedValue && this.mutatedValue !== '') {
        let emailVerif = await customerService.checkRecipient(this.mutatedValue)
        if (emailVerif.results && !emailVerif.results.valid) {
          this.warning =
            this.$t('errors.emailIsNotCorrect') +
            ' : ' +
            this.$t('errors.' + this.toCamelCase(emailVerif.results.reason))
          if (this.$refs['validation-provider-email'].errors.length == 0) {
            EventBus.emit('showBanner', { type: 'warning', message: this.warning })
          }
        }
      }
    },
    toCamelCase(str) {
      return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase())
    }
  }
}
</script>

<style lang="scss" scoped>
.basic-email-input-container {
  padding: 10px 0;

  /* Tablet (portrait) */
  @media only screen and (width >=768px) {
    padding: 15px 0;
  }

  /* Desktops and laptops */
  @media only screen and (width >=1024px) {
    padding: 15px 0;
  }

  /* Large screens */
  @media only screen and (width >=1824px) {
    padding: 15px 0;
  }

  div {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    white-space: nowrap;
    align-items: center;
    padding-left: 0.5rem;

    label {
      font-weight: bolder;
      margin-right: 1rem;
    }

    input {
      flex-grow: 1;
      border: none;
    }
  }
}

#autocompleteList {
  background: #fefefe;
  border: 1px solid #ccc;
  z-index: 4;
  left: 0;
  right: 0;
}

#autocompleteList li {
  padding: 10px;
  border-bottom: 1px solid #ccc;
  cursor: pointer;
}

#autocompleteList li:last-child {
  border: none;
}
</style>
